import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import 'highlight.js/styles/github.css';
import { assetService } from '@/services/asset-service';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';

interface MarkdownPreviewProps {
  assetId: string;
}

export const MarkdownPreview = ({ assetId }: MarkdownPreviewProps) => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMarkdownContent = async () => {
      try {
        setLoading(true);
        const content = await assetService.getAssetContent(assetId);
        setMarkdownContent(String(content || ''));
      } catch (err) {
        console.error('Error fetching markdown content:', err);
        setError('Failed to load markdown content');
        toast.error('Failed to load markdown content');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdownContent();
  }, [assetId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="prose max-w-none bg-white rounded-lg border flex-grow overflow-hidden">
        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="p-6">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                code(props) {
                  const { children, className, node, ...rest } = props;
                  const match = /language-(\w+)/.exec(className || '');
                  return match ? (
                    <code className={className} {...rest}>
                      {children}
                    </code>
                  ) : (
                    <code className="px-2 py-1 bg-gray-100 rounded-md text-sm" {...rest}>
                      {children}
                    </code>
                  );
                },
                pre(props) {
                  const { children, className, node, ...rest } = props;
                  return (
                    <pre 
                      className={`p-4 bg-gray-50 rounded-md overflow-x-auto border ${className}`} 
                      {...rest}
                    >
                      {children}
                    </pre>
                  );
                },
                h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
                h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-3" {...props} />,
                h3: ({ node, ...props }) => <h3 className="text-lg font-bold mt-4 mb-2" {...props} />,
                p: ({ node, ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                blockquote: ({ node, ...props }) => (
                  <blockquote 
                    className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-4" 
                    {...props} 
                  />
                ),
                a: ({ node, ...props }) => (
                  <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
                ),
                img: ({ node, ...props }) => (
                  <img className="max-w-full h-auto rounded-md my-4 border" {...props} />
                ),
                strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                em: ({ node, ...props }) => <em className="italic" {...props} />,
                hr: ({ node, ...props }) => <hr className="my-6 border-gray-200" {...props} />,
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};